'use client'

import avatarIcon from '@public/images/avatar.svg'
import Image from 'next/image'
import Link from 'next/link'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6Props } from '@src/components/TopNavV6/TopNavV6Props'
import { useSession } from '@src/hooks/useSession'
import { cn } from '@src/utils/tailwind'

import cartIcon from '../assets/desktop-cart-icon.svg'

export const TopNavV6Desktop = (props: TopNavV6Props) => {
  const {
    logo,
    navItems,
    cartHref = '#cart',
    joinBodiHref = '/join',
    loginLabel,
    joinBodiLabel,
    onCartClick,
    onLoginClick,
    onJoinBodiClick,
    onAccountClick,
    account,
  } = props
  const { setAccountExpanded } = useTopNavV6()
  const { user, getLoginHref } = useSession()
  const loginHref = getLoginHref()

  // Use content labels with fallbacks
  const loginButtonLabel = loginLabel ?? account?.logInCopy ?? 'Login'
  const joinBodiButtonLabel = joinBodiLabel ?? account?.bodiCta?.title ?? 'Join BODi'

  const handleAccountClick = () => {
    if (onAccountClick) {
      onAccountClick()
    } else {
      setAccountExpanded(true)
    }
  }

  return (
    <>
      <nav
        className="relative z-50 flex h-16 w-full items-center bg-bodi-black-text px-4"
        data-name="Desktop - Nav Bar - Signed In"
      >
        {/* Logo - Centered */}
        {logo?.src && (
          <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center">
            <LocalizedLink href={logo.href ?? '/'} className="flex shrink-0 items-center">
              <Image
                src={logo.src}
                width={logo.width ?? 100}
                height={logo.height ?? 33}
                alt={logo.altText ?? 'Logo'}
              />
            </LocalizedLink>
          </div>
        )}

        {/* Navigation Items - Left */}
        <div className="flex h-14 items-end self-end">
          {navItems?.map((item, index) => {
            const url = user && item.authenticatedHref ? item.authenticatedHref : (item.href ?? '#')
            return (
              <LocalizedLink
                key={`nav-item-${index}`}
                href={url}
                onClick={item.onClick}
                className={cn(
                  'box-border flex h-full items-center justify-center border-t-[3px] border-r-0 border-transparent p-4 font-lateral text-body-lg leading-none transition-colors',
                  {
                    'border-bodi-green-bright-500 text-white hover:underline': item.isActive,
                    'text-bodi-gray-butters hover:text-bodi-white': !item.isActive,
                  }
                )}
                style={
                  item.isActive
                    ? {
                        backgroundImage:
                          'linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), linear-gradient(90deg, rgba(22, 24, 25, 1) 0%, rgba(22, 24, 25, 1) 100%)',
                      }
                    : undefined
                }
              >
                {item.title}
              </LocalizedLink>
            )
          })}
        </div>

        {/* Right Side - Cart and Auth */}
        <div className={cn('ml-auto flex h-full items-center gap-4')}>
          {/* Cart Icon with Badge */}
          <div className="flex h-11 w-7 items-center justify-center">
            <Link
              className="relative"
              data-testid="cartBtn"
              href={cartHref}
              onClick={onCartClick}
              scroll={false}
            >
              <Image
                src={cartIcon}
                width={300}
                height={150}
                alt="Shopping cart"
                className="h-6 w-6 object-cover"
              />
              {/* TODO: Add cart quantity badge once we have Shopify cart sync working*/}
              {/* {cartQuantity > 0 ? (
                <div
                  className={cn(
                    'absolute top-0 right-[-10px] -mt-2 flex min-h-[23px] min-w-[23px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                    { 'px-1.5': cartQuantity > 99 }
                  )}
                >
                  {cartQuantity > 99 ? '99' : cartQuantity}
                </div>
              ) : null} */}
            </Link>
          </div>
          {/* Login and Join BODi Buttons */}
          {!user ? (
            <div className="flex items-center gap-2">
              <a href={loginHref} onClick={onLoginClick}>
                <Button
                  size="small"
                  variant="black"
                  className="h-auto border border-solid border-bodi-gray-medium px-4 pt-[6px] pb-[5px]"
                  contentClassName="px-0 text-body-md font-semibold leading-[18px] tracking-normal"
                >
                  {loginButtonLabel}
                </Button>
              </a>
              <LocalizedLink href={joinBodiHref} onClick={onJoinBodiClick}>
                <Button
                  size="small"
                  className="h-auto border border-x-0 border-solid border-bodi-black-text px-4 pt-[6px] pb-[5px]"
                  contentClassName="px-0 text-body-md font-semibold leading-[18px] tracking-normal"
                >
                  {joinBodiButtonLabel}
                </Button>
              </LocalizedLink>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <button
                onClick={handleAccountClick}
                data-testid="accountBtn"
                className="ml-2 flex h-[22px] w-[22px] items-center justify-center"
              >
                <Image src={avatarIcon} width={22} height={22} alt="Account icon" />
              </button>
              {!user?.hasBodiAccess && (
                <LocalizedLink href={joinBodiHref} onClick={onJoinBodiClick}>
                  <Button
                    size="small"
                    className="h-auto border border-x-0 border-solid border-bodi-black-text px-4 pt-[6px] pb-[5px]"
                    contentClassName="px-0 text-body-md font-semibold leading-[18px] tracking-normal"
                  >
                    {joinBodiButtonLabel}
                  </Button>
                </LocalizedLink>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  )
}
